// ./src/hooks/useSavedPlaces.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import {
  SavedPlace,
  SavedPlaceService,
  SavedPlaceFilter,
  SavedPlaceInput,
  SavedPlaceProximityInput,
  Pagination,
  SortItem,
  sortSavedPlacesByRelevance,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseSavedPlacesOptions {
  /**
   * Page size for pagination.
   * Mount-time configuration value.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration value.
   * @default { status: [STATUSES.ACTIVE] }
   */
  initialFilters?: Partial<SavedPlaceFilter>;

  /**
   * Sort configuration applied on initial autoLoad and userId-change reloads.
   * Mount-time configuration value.
   * @default [{ name: "useCount", order: "DESC" }, { name: "name", order: "ASC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseSavedPlacesReturn {
  // Data state
  savedPlace: SavedPlace | null;
  savedPlaces: SavedPlace[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<SavedPlaceFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedSavedPlace: SavedPlace | null;

  // External mutation notifications
  /** Notify that a place was created or updated externally. Updates in-place if exists, prepends if new. */
  notifyPlaceChanged: (place: SavedPlace) => void;
  /** Notify that a place was removed externally. Removes from the cached list by ID. */
  notifyPlaceRemoved: (id: string) => void;

  // Core operations
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<SavedPlaceFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean,
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: SavedPlaceInput) => Promise<OpResult>;
  update: (id: string, params: SavedPlaceInput) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Proximity search
  listByProximity: (
    proximity: SavedPlaceProximityInput,
    filter?: Partial<SavedPlaceFilter>,
  ) => Promise<OpResult>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedSavedPlace: (place: SavedPlace | null) => void;

  // Helper operations
  reset: () => void;

  // Lookup helpers
  getSavedPlaceById: (id: string | undefined) => SavedPlace | undefined;

  // Sorting helpers
  getSortedByRelevance: (travelType?: string) => SavedPlace[];

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [
  { name: "useCount", order: "DESC" },
  { name: "name", order: "ASC" },
];
const DEFAULT_FILTERS: Partial<SavedPlaceFilter> = { status: [STATUSES.ACTIVE] };

// ============================================
// Hook Implementation
// ============================================

export const useSavedPlaces = (
  options: UseSavedPlacesOptions = {},
): UseSavedPlacesReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  const { user } = useApp();
  const userId = user?.id ?? null;

  // Mount-time configuration refs
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Refs for loadMore/refresh
  const lastQueriedFiltersRef = useRef<Partial<SavedPlaceFilter>>(initialFilters);
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);
  const listCallIdRef = useRef(0);
  const previousUserIdRef = useRef<string | null | undefined>(undefined);
  const currentPageRef = useRef(1);

  // Data state
  const [savedPlace, setSavedPlace] = useState<SavedPlace | null>(null);
  const [savedPlaces, setSavedPlaces] = useState<SavedPlace[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters
  const [filters, setFilters] = useState<Partial<SavedPlaceFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedSavedPlace, setSelectedSavedPlace] = useState<SavedPlace | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed
  const isEmpty = useMemo(
    () => isLoaded && savedPlaces.length === 0,
    [isLoaded, savedPlaces.length],
  );

  // ==========================================
  // List Helpers
  // ==========================================

  const updateOrAddInList = useCallback((updatedItem: SavedPlace) => {
    setSavedPlaces((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === updatedItem.id);
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  const removeFromList = useCallback((id: string) => {
    setSavedPlaces((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setSavedPlaces((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  const notifyPlaceChanged = useCallback((place: SavedPlace) => {
    updateOrAddInList(place);
  }, [updateOrAddInList]);

  const notifyPlaceRemoved = useCallback((id: string) => {
    removeFromList(id);
  }, [removeFromList]);

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  const reset = useCallback(() => {
    listCallIdRef.current++;
    setSavedPlace(null);
    setSavedPlaces([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedSavedPlace(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await SavedPlaceService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as SavedPlace[])[0];
          setSavedPlace(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load saved place"));
        setSavedPlace(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load saved place"));
        setSavedPlace(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList],
  );

  const list = useCallback(
    async (
      filter: Partial<SavedPlaceFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true,
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setCurrentPage(1);
        currentPageRef.current = 1;
      }

      setLastError(null);

      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await SavedPlaceService.list(
          filter as SavedPlaceFilter,
          paginationParams,
          sortParams,
        );

        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as SavedPlace[])
            : [];

          if (isAppending) {
            setSavedPlaces((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id),
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setSavedPlaces(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          currentPageRef.current = targetPage;
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load saved places"));
        setIsLoaded(true);
        if (!isAppending) setSavedPlaces([]);
        return result;
      } catch (error) {
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load saved places"));
        setIsLoaded(true);
        if (!isAppending) setSavedPlaces([]);
        return result;
      } finally {
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    [],
  );

  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await SavedPlaceService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as SavedPlace[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load saved places"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load saved places"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList],
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  const create = useCallback(
    async (params: SavedPlaceInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await SavedPlaceService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as SavedPlace[])[0];
          setSavedPlace(newItem);
          setSavedPlaces((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create saved place"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create saved place"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    [],
  );

  const update = useCallback(
    async (id: string, params: SavedPlaceInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Saved place ID is required for updating",
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await SavedPlaceService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as SavedPlace[])[0];
          setSavedPlace(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update saved place"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update saved place"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList],
  );

  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "Saved place ID is required for removal",
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await SavedPlaceService.remove(id);

        if (result.code === OP_RESULT_CODES.OK) {
          setSavedPlace(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove saved place"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove saved place"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList],
  );

  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one saved place ID is required for removal",
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      try {
        const result = await SavedPlaceService.removeMany(ids);

        if (result.code === OP_RESULT_CODES.OK) {
          setSavedPlace(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove saved places"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove saved places"));
        return result;
      }
    },
    [removeManyFromList],
  );

  // ==========================================
  // Proximity Search
  // ==========================================

  const listByProximity = useCallback(
    async (
      proximity: SavedPlaceProximityInput,
      filter?: Partial<SavedPlaceFilter>,
    ): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await SavedPlaceService.listByProximity(
          proximity,
          filter as SavedPlaceFilter,
        );

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          // const items = Array.isArray(result.data)
          //   ? (result.data as SavedPlace[])
          //   : [];
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to find nearby saved places"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to find nearby saved places"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPageRef.current + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false,
    );
  }, [list, loadingMore, hasMore]);

  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true,
    );
  }, [list]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  const getSavedPlaceById = useCallback(
    (id: string | undefined): SavedPlace | undefined => {
      if (!id) return undefined;
      return savedPlaces.find((item) => item.id === id);
    },
    [savedPlaces],
  );

  /**
   * Get saved places sorted by relevance for a given travel type.
   * Groups by place type priority, then by useCount, then by name.
   */
  const getSortedByRelevance = useCallback(
    (travelType?: string): SavedPlace[] => {
      return sortSavedPlacesByRelevance(savedPlaces, travelType);
    },
    [savedPlaces],
  );

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    if (previousUserId === undefined) {
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true,
        );
      }
      return;
    }

    if (previousUserId !== userId) {
      reset();

      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true,
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      savedPlace,
      savedPlaces,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      selectedSavedPlace,

      notifyPlaceChanged,
      notifyPlaceRemoved,

      // Core operations
      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,

      // Proximity search
      listByProximity,

      // Additional convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedSavedPlace,

      // Helper operations
      reset,

      // Lookup helpers
      getSavedPlaceById,

      // Sorting helpers
      getSortedByRelevance,

      // Error handling
      lastError,
      clearError,
    }),
    [
      savedPlace,
      savedPlaces,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      selectedSavedPlace,

      notifyPlaceChanged,
      notifyPlaceRemoved,

      get,
      list,
      getMany,
      create,
      update,
      remove,
      removeMany,
      listByProximity,
      loadMore,
      refresh,
      getSavedPlaceById,
      getSortedByRelevance,
      lastError,
      clearError,
      reset,
    ],
  );
};